import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['generateBtn', 'deployBtn', 'progress', 'progressText']

  static values = {
    websiteId: String,
    generateUrl: String,
    deployUrl: String,
    csrf: String
  }

  async generate() {
    if (!confirm('Generate all queued location pages? This may take a few minutes.')) {
      return
    }

    this.setLoading(true, 'Queueing page generation...')

    try {
      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast(data.message || 'Page generation started!', 'success')
        this.setLoading(true, 'Generation in progress... Page will refresh when complete.')

        // Poll for completion or just reload after a delay
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        this.showToast(data.error || 'Failed to start generation', 'error')
        this.setLoading(false)
      }
    } catch (error) {
      console.error('Generation error:', error)
      this.showToast('Failed to start generation', 'error')
      this.setLoading(false)
    }
  }

  async deploy() {
    if (!confirm('Deploy the website with all generated pages?')) {
      return
    }

    this.setLoading(true, 'Starting deployment...')

    try {
      const response = await fetch(this.deployUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showToast(data.message || 'Deployment started!', 'success')
        this.setLoading(true, 'Deployment in progress...')

        // Redirect to deployment page or reload
        if (data.redirect_url) {
          setTimeout(() => {
            window.location.href = data.redirect_url
          }, 1500)
        } else {
          setTimeout(() => {
            window.location.reload()
          }, 3000)
        }
      } else {
        this.showToast(data.error || 'Failed to start deployment', 'error')
        this.setLoading(false)
      }
    } catch (error) {
      console.error('Deployment error:', error)
      this.showToast('Failed to start deployment', 'error')
      this.setLoading(false)
    }
  }

  setLoading(loading, message = 'Processing...') {
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = loading
      if (loading) {
        this.generateBtnTarget.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        this.generateBtnTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    }

    if (this.hasDeployBtnTarget) {
      this.deployBtnTarget.disabled = loading
      if (loading) {
        this.deployBtnTarget.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        this.deployBtnTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    }

    if (this.hasProgressTarget) {
      if (loading) {
        this.progressTarget.classList.remove('hidden')
      } else {
        this.progressTarget.classList.add('hidden')
      }
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = message
    }
  }

  showToast(message, type = 'success') {
    const toast = document.createElement('div')
    toast.style.cssText = `
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      z-index: 9999;
      animation: slideInUp 0.3s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      ${type === 'success' ?
    'background: #10b981; color: white;' :
    'background: #ef4444; color: white;'}
    `

    const icon = type === 'success' ?
      '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' :
      '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'

    toast.innerHTML = `${icon}<span>${message}</span>`

    if (!document.getElementById('toast-styles')) {
      const style = document.createElement('style')
      style.id = 'toast-styles'
      style.textContent = `
        @keyframes slideInUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideOutDown {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(100%); opacity: 0; }
        }
      `
      document.head.appendChild(style)
    }

    document.body.appendChild(toast)

    setTimeout(() => {
      toast.style.animation = 'slideOutDown 0.3s ease forwards'
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
